import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    DeleteOutlined,
    ExportOutlined,
    SearchOutlined,
    StarFilled,
    StarOutlined,
} from '@ant-design/icons';
import { Button, Checkbox, Empty, Input, Modal, Popover, Select, Spin, Tabs, message } from 'antd';
import TemplatePreviewCard from '../TemplatePreviewCard';
import {
    deleteFlowTemplate,
    favoriteFlowTemplate,
    getFlowTemplateCreators,
    getFlowTemplatePage,
    getFlowTemplateStudios,
    publishFlowTemplate,
} from '../../execution';
import { AppContext } from '@utils/AppContext';
import styles from './index.module.scss';

const BASE_URL = 'https://photostudio-ai-oss.arcsoft.com.cn//photostudio-opshub/FreeCanvas';
const SEND_ICON = `${BASE_URL}/send.svg`;
const PAGE_SIZE = 20;
const TEMPLATE_SCOPE_ITEMS = [
    { key: 'personal', label: '个人模板' },
    { key: 'public', label: '公共模板' },
    { key: 'favorite', label: '我的收藏' },
];

const createScopeState = () => ({
    items: [],
    pageIndex: 0,
    total: 0,
    totalPages: 0,
    loading: false,
    loaded: false,
    hasMore: true,
    queryKey: '',
});

const createInitialScopeMap = () => TEMPLATE_SCOPE_ITEMS.reduce((acc, item) => ({
    ...acc,
    [item.key]: createScopeState(),
}), {});

const formatTemplateDate = (value = '') => {
    if (!value) return '';
    const matched = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!matched) return value;
    return `${matched[1]}/${matched[2]}/${matched[3]}`;
};

const formatTemplateMeta = (item = {}) => {
    const parts = [item?.creatorName, item?.studioName].filter(Boolean);
    if (parts.length) return parts.join('·');
    return item?.createdBy || '--';
};

const CARD_ACTIONS = [
    {
        key: 'apply',
        label: '应用到画布',
        shortLabel: '使用',
        icon: <img src={SEND_ICON} alt="" />,
        primary: true,
    },
    {
        key: 'favorite',
        label: '收藏',
        shortLabel: '收藏',
    },
    {
        key: 'publish',
        label: '发送至公共模板库',
        shortLabel: '发布',
        icon: <ExportOutlined />,
        visible: (item) => !!item?.canPublish,
    },
    {
        key: 'delete',
        label: '删除',
        shortLabel: '删除',
        icon: <DeleteOutlined />,
        visible: (item) => !!item?.canDelete,
    },
];

const TemplateLibraryModal = ({
    open = false,
    onClose,
    onApply,
}) => {
    const { userInfo } = useContext(AppContext);
    const requestIdRef = useRef({});
    const prevOpenRef = useRef(false);
    const prevScopeRef = useRef('personal');
    const prevPublicQueryKeyRef = useRef('');
    const [activeScope, setActiveScope] = useState('personal');
    const [scopeStateMap, setScopeStateMap] = useState(createInitialScopeMap);
    const [publicStudioOptions, setPublicStudioOptions] = useState([]);
    const [publicCreatorOptions, setPublicCreatorOptions] = useState([]);
    const [publicFilterLoading, setPublicFilterLoading] = useState(false);
    const [publicFiltersLoaded, setPublicFiltersLoaded] = useState(false);
    const [publicStudioId, setPublicStudioId] = useState();
    const [publicCreator, setPublicCreator] = useState();
    const [publicKeywordInput, setPublicKeywordInput] = useState('');
    const [publicKeyword, setPublicKeyword] = useState('');
    const [applyPendingMap, setApplyPendingMap] = useState({});
    const [favoritePendingMap, setFavoritePendingMap] = useState({});
    const [publishPendingMap, setPublishPendingMap] = useState({});
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteCascade, setDeleteCascade] = useState(false);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);
    const activeScopeState = scopeStateMap[activeScope] || createScopeState();
    const currentUserId = String(userInfo?.userId ?? '');
    const publicFilterQueryKey = useMemo(() => JSON.stringify({
        studioId: publicStudioId || '',
        createdBy: publicCreator || '',
        keyword: publicKeyword || '',
    }), [publicCreator, publicKeyword, publicStudioId]);
    const canOperateTemplate = useCallback((templateItem) => {
        const createdBy = String(templateItem?.createdBy ?? '');
        if (!createdBy || !currentUserId) return false;
        return createdBy === currentUserId;
    }, [currentUserId]);
    const canManageTemplate = useCallback((templateItem) => canOperateTemplate(templateItem), [canOperateTemplate]);

    const loadTemplates = useCallback(async ({
        scope = 'personal',
        pageIndex = 1,
        append = false,
    } = {}) => {
        let canRequest = true;
        setScopeStateMap((prev) => {
            const currentScopeState = prev[scope] || createScopeState();
            if (currentScopeState.loading) {
                canRequest = false;
                return prev;
            }

            return {
                ...prev,
                [scope]: {
                    ...currentScopeState,
                    loading: true,
                    loaded: append ? currentScopeState.loaded : false,
                    queryKey: scope === 'public' ? publicFilterQueryKey : currentScopeState.queryKey,
                },
            };
        });

        if (!canRequest) return;

        const requestId = Date.now();
        requestIdRef.current[scope] = requestId;

        try {
            const pageData = await getFlowTemplatePage({
                scope,
                pageIndex,
                pageSize: PAGE_SIZE,
                studioId: scope === 'public' ? publicStudioId : undefined,
                createdBy: scope === 'public' ? publicCreator : undefined,
                keyword: scope === 'public' ? publicKeyword : '',
            });

            if (requestIdRef.current[scope] !== requestId) return;

            setScopeStateMap((prev) => {
                const currentScopeState = prev[scope] || createScopeState();
                const nextItems = append
                    ? [...currentScopeState.items, ...(pageData.items || [])]
                    : (pageData.items || []);
                const totalPages = Number(pageData.totalPages ?? 0);
                const nextPageIndex = Number(pageData.pageIndex ?? pageIndex);

                return {
                    ...prev,
                    [scope]: {
                        items: nextItems,
                        pageIndex: nextPageIndex,
                        total: Number(pageData.total ?? nextItems.length),
                        totalPages,
                        loading: false,
                        loaded: true,
                        hasMore: totalPages > 0
                            ? nextPageIndex < totalPages
                            : nextItems.length < Number(pageData.total ?? 0),
                        queryKey: scope === 'public' ? publicFilterQueryKey : '',
                    },
                };
            });
        } catch (error) {
            if (requestIdRef.current[scope] !== requestId) return;

            setScopeStateMap((prev) => {
                const currentScopeState = prev[scope] || createScopeState();

                return {
                    ...prev,
                    [scope]: {
                        ...currentScopeState,
                        items: append ? currentScopeState.items : [],
                        total: append ? currentScopeState.total : 0,
                        totalPages: append ? currentScopeState.totalPages : 0,
                        loading: false,
                        loaded: true,
                        hasMore: false,
                        queryKey: scope === 'public' ? publicFilterQueryKey : '',
                    },
                };
            });
        }
    }, [publicCreator, publicFilterQueryKey, publicKeyword, publicStudioId]);

    useEffect(() => {
        if (!open) return;
        const justOpened = !prevOpenRef.current;
        const scopeChanged = prevScopeRef.current !== activeScope;
        const publicFilterChanged = activeScope === 'public'
            && prevPublicQueryKeyRef.current !== publicFilterQueryKey;

        prevOpenRef.current = true;
        prevScopeRef.current = activeScope;
        prevPublicQueryKeyRef.current = publicFilterQueryKey;

        if (!justOpened && !scopeChanged && !publicFilterChanged) return;
        if (activeScopeState.loading) return;
        loadTemplates({
            scope: activeScope,
            pageIndex: 1,
            append: false,
        });
    }, [
        activeScope,
        activeScopeState.loading,
        loadTemplates,
        open,
        publicFilterQueryKey,
    ]);

    useEffect(() => {
        if (!open) {
            requestIdRef.current = {};
            prevOpenRef.current = false;
            prevScopeRef.current = 'personal';
            prevPublicQueryKeyRef.current = '';
            setActiveScope('personal');
            setScopeStateMap(createInitialScopeMap());
            setPublicStudioOptions([]);
            setPublicCreatorOptions([]);
            setPublicFilterLoading(false);
            setPublicFiltersLoaded(false);
            setPublicStudioId(undefined);
            setPublicCreator(undefined);
            setPublicKeywordInput('');
            setPublicKeyword('');
            setApplyPendingMap({});
            setFavoritePendingMap({});
            setPublishPendingMap({});
            setDeleteTarget(null);
            setDeleteCascade(false);
            setDeleteSubmitting(false);
        }
    }, [open]);

    useEffect(() => {
        if (!open || activeScope !== 'public' || publicFiltersLoaded || publicFilterLoading) return;

        setPublicFilterLoading(true);
        Promise.all([
            getFlowTemplateStudios(),
            getFlowTemplateCreators(),
        ]).then(([studios, creators]) => {
            setPublicStudioOptions(Array.isArray(studios) ? studios : []);
            setPublicCreatorOptions(
                Array.isArray(creators)
                    ? creators.map((item) => ({
                        id: String(item?.id ?? ''),
                        username: item?.username || '',
                    }))
                    : [],
            );
            setPublicFiltersLoaded(true);
        }).finally(() => {
            setPublicFilterLoading(false);
        });
    }, [activeScope, open, publicFilterLoading, publicFiltersLoaded]);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setPublicKeyword(publicKeywordInput.trim());
        }, 300);

        return () => window.clearTimeout(timer);
    }, [publicKeywordInput]);

    const handleTabChange = useCallback((nextScope) => {
        setActiveScope(nextScope);
    }, []);

    const handleToggleFavorite = useCallback(async (templateItem) => {
        const templateId = String(templateItem?.id || '');
        if (!templateId || favoritePendingMap[templateId]) return;

        const nextFavorite = !templateItem?.favorite;
        setFavoritePendingMap((prev) => ({
            ...prev,
            [templateId]: true,
        }));

        setScopeStateMap((prev) => {
            const nextState = { ...prev };

            Object.keys(nextState).forEach((scopeKey) => {
                const scopeState = nextState[scopeKey] || createScopeState();
                const currentItems = Array.isArray(scopeState.items) ? scopeState.items : [];
                let nextItems = currentItems.map((item) => (
                    String(item?.id) === templateId
                        ? { ...item, favorite: nextFavorite }
                        : item
                ));
                let nextTotal = scopeState.total;

                if (scopeKey === 'favorite' && !nextFavorite) {
                    const removedCount = currentItems.some((item) => String(item?.id) === templateId) ? 1 : 0;
                    nextItems = nextItems.filter((item) => item.favorite);
                    nextTotal = Math.max(0, Number(scopeState.total ?? currentItems.length) - Math.max(removedCount, 0));
                }

                nextState[scopeKey] = {
                    ...scopeState,
                    items: nextItems,
                    total: nextTotal,
                };
            });

            return nextState;
        });

        try {
            await favoriteFlowTemplate(templateId);
        } catch (error) {
            setScopeStateMap((prev) => {
                const nextState = { ...prev };

                Object.keys(nextState).forEach((scopeKey) => {
                    const scopeState = nextState[scopeKey] || createScopeState();
                    const currentItems = Array.isArray(scopeState.items) ? scopeState.items : [];
                    let nextItems = currentItems.map((item) => (
                        String(item?.id) === templateId
                            ? { ...item, favorite: !!templateItem?.favorite }
                            : item
                    ));
                    let nextTotal = scopeState.total;

                    if (scopeKey === 'favorite' && !templateItem?.favorite) {
                        const exists = nextItems.some((item) => String(item?.id) === templateId);
                        if (!exists) {
                            nextItems = [templateItem, ...nextItems];
                            nextTotal = Number(scopeState.total ?? currentItems.length) + 1;
                        }
                    }

                    nextState[scopeKey] = {
                        ...scopeState,
                        items: nextItems,
                        total: nextTotal,
                    };
                });

                return nextState;
            });
        } finally {
            setFavoritePendingMap((prev) => {
                const nextMap = { ...prev };
                delete nextMap[templateId];
                return nextMap;
            });
        }
    }, [favoritePendingMap]);

    const handleScopeScroll = useCallback((event) => {
        const currentScopeState = scopeStateMap[activeScope] || createScopeState();
        if (currentScopeState.loading || !currentScopeState.hasMore) return;

        const target = event.currentTarget;
        const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
        if (remaining > 120) return;

        loadTemplates({
            scope: activeScope,
            pageIndex: currentScopeState.pageIndex + 1,
            append: true,
        });
    }, [activeScope, loadTemplates, scopeStateMap]);

    const handleApplyTemplate = useCallback(async (templateItem) => {
        const templateId = String(templateItem?.id || '');
        if (!templateId || !onApply || applyPendingMap[templateId]) return;

        setApplyPendingMap((prev) => ({
            ...prev,
            [templateId]: true,
        }));

        try {
            await onApply(templateItem);
        } finally {
            setApplyPendingMap((prev) => {
                const nextMap = { ...prev };
                delete nextMap[templateId];
                return nextMap;
            });
        }
    }, [applyPendingMap, onApply]);

    const handlePublishTemplate = useCallback(async (templateItem) => {
        const templateId = String(templateItem?.id || '');
        if (!templateId || publishPendingMap[templateId] || !canManageTemplate(templateItem)) return;

        setPublishPendingMap((prev) => ({
            ...prev,
            [templateId]: true,
        }));

        try {
            await publishFlowTemplate(templateId);
            message.success('已发送至公共模板库');
            setScopeStateMap((prev) => {
                const nextState = { ...prev };

                Object.keys(nextState).forEach((scopeKey) => {
                    const scopeState = nextState[scopeKey] || createScopeState();
                    nextState[scopeKey] = {
                        ...scopeState,
                        items: (scopeState.items || []).map((item) => (
                            String(item?.id) === templateId
                                ? {
                                    ...item,
                                    canPublish: false,
                                }
                                : item
                        )),
                    };
                });

                return nextState;
            });
        } finally {
            setPublishPendingMap((prev) => {
                const nextMap = { ...prev };
                delete nextMap[templateId];
                return nextMap;
            });
        }
    }, [canManageTemplate, publishPendingMap]);

    const openDeleteDialog = useCallback((templateItem) => {
        if (!canManageTemplate(templateItem)) return;
        setDeleteTarget(templateItem);
        setDeleteCascade(false);
    }, [canManageTemplate]);

    const closeDeleteDialog = useCallback(() => {
        if (deleteSubmitting) return;
        setDeleteTarget(null);
        setDeleteCascade(false);
    }, [deleteSubmitting]);

    const handleConfirmDelete = useCallback(async () => {
        const templateId = String(deleteTarget?.id || '');
        if (!templateId || deleteSubmitting) return;

        setDeleteSubmitting(true);

        try {
            await deleteFlowTemplate({
                templateId,
                cascade: activeScope === 'personal' ? deleteCascade : false,
            });

            setScopeStateMap((prev) => {
                const nextState = { ...prev };

                Object.keys(nextState).forEach((scopeKey) => {
                    const scopeState = nextState[scopeKey] || createScopeState();
                    const currentItems = Array.isArray(scopeState.items) ? scopeState.items : [];
                    const exists = currentItems.some((item) => String(item?.id) === templateId);
                    if (!exists) return;

                    nextState[scopeKey] = {
                        ...scopeState,
                        items: currentItems.filter((item) => String(item?.id) !== templateId),
                        total: Math.max(0, Number(scopeState.total ?? currentItems.length) - 1),
                    };
                });

                return nextState;
            });

            setDeleteTarget(null);
            setDeleteCascade(false);
        } finally {
            setDeleteSubmitting(false);
        }
    }, [activeScope, deleteCascade, deleteSubmitting, deleteTarget]);

    const tabItems = useMemo(() => TEMPLATE_SCOPE_ITEMS.map((item) => ({
        key: item.key,
        label: item.label,
    })), []);

    return (
        <Modal
            open={open}
            title="模板库"
            footer={null}
            width={1080}
            onCancel={onClose}
            className={styles.modal}
            destroyOnClose
        >
            <div className={styles.body}>
                <Tabs
                    activeKey={activeScope}
                    items={tabItems}
                    onChange={handleTabChange}
                    className={styles.tabs}
                />
                {activeScope === 'public' ? (
                    <div className={styles.filterBar}>
                        <Select
                            className={styles.filterSelect}
                            placeholder="请选择工作室"
                            fieldNames={{ value: 'id', label: 'name' }}
                            options={publicStudioOptions}
                            optionFilterProp="name"
                            allowClear
                            showSearch
                            loading={publicFilterLoading}
                            value={publicStudioId}
                            onChange={setPublicStudioId}
                        />
                        <Select
                            className={styles.filterSelect}
                            placeholder="请选择创建人"
                            fieldNames={{ value: 'id', label: 'username' }}
                            options={publicCreatorOptions}
                            optionFilterProp="username"
                            allowClear
                            showSearch
                            loading={publicFilterLoading}
                            value={publicCreator}
                            onChange={setPublicCreator}
                        />
                        <Input
                            className={styles.filterSearch}
                            placeholder="搜索模板名称"
                            prefix={<SearchOutlined />}
                            allowClear
                            value={publicKeywordInput}
                            onChange={(event) => setPublicKeywordInput(event.target.value)}
                        />
                    </div>
                ) : null}
                <div className={styles.tabPanel} onScroll={handleScopeScroll}>
                    {activeScopeState.loading && !activeScopeState.items.length ? (
                        <div className={styles.loading}>
                            <Spin size="large" />
                        </div>
                    ) : activeScopeState.items.length ? (
                        <>
                            <div className={styles.grid}>
                                {activeScopeState.items.map((item) => (
                                    <div key={item.id} className={styles.card}>
                                        {item.favorite ? (
                                            <div className={styles.favoriteBadge}>
                                                <StarFilled />
                                            </div>
                                        ) : null}
                                        <div className={styles.cardActions}>
                                            {CARD_ACTIONS.filter((action) => (
                                                action.key === 'apply'
                                                    ? true
                                                    : action.key === 'favorite'
                                                        ? true
                                                        : canManageTemplate(item) && (typeof action.visible === 'function' ? action.visible(item) : true)
                                            )).map((action) => {
                                                const iconNode = action.key === 'favorite'
                                                    ? (item.favorite ? <StarFilled className={styles.favoriteActiveIcon} /> : <StarOutlined />)
                                                    : action.icon;
                                                const applyPending = !!applyPendingMap[item.id];
                                                const favoritePending = !!favoritePendingMap[item.id];
                                                const publishPending = !!publishPendingMap[item.id];
                                                const canManageCurrentTemplate = canManageTemplate(item);
                                                const buttonNode = (
                                                    <button
                                                        key={action.key}
                                                        type="button"
                                                        className={`${styles.cardActionButton} ${action.primary ? styles.primaryActionButton : ''} ${action.key === 'favorite' && item.favorite ? styles.favoriteActionButton : ''}`}
                                                        aria-label={action.key === 'favorite' && item.favorite ? '取消收藏' : action.label}
                                                        disabled={
                                                            action.key === 'apply'
                                                                ? applyPending
                                                                : action.key !== 'favorite' && !canManageCurrentTemplate
                                                                    ? true
                                                                : action.key === 'favorite'
                                                                    ? favoritePending
                                                                    : action.key === 'publish'
                                                                        ? publishPending
                                                                        : false
                                                        }
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            if (action.key === 'apply') {
                                                                handleApplyTemplate(item);
                                                            }
                                                            if (action.key === 'favorite') {
                                                                handleToggleFavorite(item);
                                                            }
                                                            if (!canManageCurrentTemplate) return;
                                                            if (action.key === 'publish') {
                                                                handlePublishTemplate(item);
                                                            }
                                                            if (action.key === 'delete') {
                                                                openDeleteDialog(item);
                                                            }
                                                        }}
                                                    >
                                                        <span className={styles.cardActionIcon}>{iconNode}</span>
                                                        {action.primary ? (
                                                            <span className={styles.cardActionText}>
                                                                {action.shortLabel}
                                                            </span>
                                                        ) : null}
                                                    </button>
                                                );

                                                return (
                                                    <Popover
                                                        key={action.key}
                                                        content={action.key === 'favorite' && item.favorite ? '取消收藏' : action.label}
                                                        trigger="hover"
                                                        placement="top"
                                                        overlayClassName={styles.cardActionPopover}
                                                    >
                                                        {buttonNode}
                                                    </Popover>
                                                );
                                            })}
                                        </div>
                                        <TemplatePreviewCard
                                            cover={item.cover}
                                            templateName={item.templateName}
                                        />
                                        <div className={styles.meta}>
                                            <div className={styles.name}>{item.templateName}</div>
                                            <div className={styles.desc}>
                                                <span>{formatTemplateMeta(item)}</span>
                                                <span>{formatTemplateDate(item.updatedTime || item.createdTime)}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {activeScopeState.loading ? (
                                <div className={styles.loadMore}>
                                    <Spin size="small" />
                                </div>
                            ) : null}
                        </>
                    ) : (
                        <div className={styles.emptyWrap}>
                            <Empty description="暂无模板" />
                        </div>
                    )}
                </div>
            </div>
            <Modal
                open={!!deleteTarget}
                title="提示"
                footer={null}
                width={470}
                centered
                onCancel={closeDeleteDialog}
                closable={!deleteSubmitting}
                maskClosable={!deleteSubmitting}
                destroyOnClose
                className={styles.deleteTemplateModal}
            >
                <div className={styles.deleteTemplateContent}>
                    <div className={styles.deleteTemplateText}>确认删除该模板?</div>
                    {activeScope === 'personal' ? (
                        <Checkbox
                            checked={deleteCascade}
                            onChange={(event) => setDeleteCascade(event.target.checked)}
                            className={styles.deleteTemplateCheckbox}
                            disabled={deleteSubmitting}
                        >
                            同步删除公共模板
                        </Checkbox>
                    ) : null}
                    <div className={styles.deleteTemplateActions}>
                        <Button
                            className={styles.deleteTemplateCancel}
                            onClick={closeDeleteDialog}
                            disabled={deleteSubmitting}
                        >
                            取消
                        </Button>
                        <Button
                            type="primary"
                            className={styles.deleteTemplateConfirm}
                            onClick={handleConfirmDelete}
                            loading={deleteSubmitting}
                        >
                            确认
                        </Button>
                    </div>
                </div>
            </Modal>
        </Modal>
    );
};

export default TemplateLibraryModal;
